import { usePropsValue } from '@hooks/UsePropsValue';
import { Input, Tag } from '@tinper/m';
import { withNativeProps } from '@utils/NativeProps';
import { mergeProps } from '@utils/WithDefaultProps';
import classNames from 'classnames';
import React from 'react';
import type {
  HyperlinksProps,
  LinkType
} from './iHyperlinks';
import SelectProtocol from './SelectProtocol';
import { getLinkValue, parseLink } from './utils';

const defaultProps = {
  browser: false,
  hideProtocol: false,
  hideText: false,
  disabled: false,
  readOnly: false,
  visible: true,
  clsPrefix: 'mui',
};

function Hyperlinks(p: HyperlinksProps) {
  const props = mergeProps(defaultProps, p);
  const { clsPrefix, hideProtocol, hideText, fieldid, browser, readOnly } = props;
  const classPrefix = `${clsPrefix}-hyperlinks`;

  const linkClassName = classNames({
    [`${classPrefix}`]: true,
    [`${classPrefix}-disabled`]: props.disabled,
  });

  const onChange = (val: LinkType) => {
    props.onChange?.(getLinkValue({ ...val, hideProtocol }));
  };

  const [value, setValue] = usePropsValue<LinkType>({
    value: parseLink(props.value, hideProtocol),
    defaultValue: parseLink(props.defaultValue, hideProtocol) ?? {},
    onChange,
  });

  const { linkText, linkAddress, protocol = 'https:' } = value ?? {};

  const onValueChange = (param: any, type: 'protocol' | 'linkAddress' | 'linkText') => {
    setValue({ ...value, [type]: type === 'protocol' ? param.key : param });
  };

  const onBlur = () => {
    props.onBlur?.(getLinkValue({ ...value, hideProtocol }));
  };

  const onFocus = () => {
    props.onFocus?.(getLinkValue({ ...value, hideProtocol }));
  };

  const onClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    // 如果隐藏协议，说明用户会自行协商协议，则直接使用 linkAddress
    const url = hideProtocol ? linkAddress || '' : `${protocol}//${linkAddress}`;
    if (props.onClick) {
      props.onClick(event, url);
    } else {
      linkAddress && (window.location.href = url);
    }
  };

  if (!props.visible) return null;

  if (readOnly || browser) {
    return withNativeProps(
      props,
      <div className={`${classPrefix}-link-label`} fieldid={fieldid}>
        <span onClick={onClick}>
          <Tag
            style={{ fontSize: '0.28rem' }}
            color="message"
            fill="none"
            label={linkText || linkAddress}
            lineNum={5}
          />
        </span>
      </div>,
    );
  }

  return withNativeProps(
    props,
    <div className={linkClassName} fieldid={fieldid}>
      <div className={`${classPrefix}-link`}>
        {
          hideProtocol ? null : <SelectProtocol
            fieldid={`${fieldid}-protocol`}
            classPrefix={classPrefix}
            disabled={props.disabled}
            protocol={protocol}
            onSelect={(param) => {
              onValueChange(param, 'protocol')
            }}
          />
        }
        <Input
          fieldid={`${fieldid}-linkAddress`}
          showClose
          disabled={props.disabled}
          value={linkAddress}
          placeholder={props.linkAddressPlaceholder}
          onChange={(param: string) => {
            onValueChange(param, 'linkAddress')
          }}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </div>
      {
        hideText ? null : <Input
          fieldid={`${fieldid}-linkText`}
          showClose
          disabled={props.disabled}
          value={linkText}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={props.linkTextPlaceholder}
          onChange={(param: string) => {
            onValueChange(param, 'linkText')
          }}
        />
      }
    </div>,
  );
}

export default Hyperlinks;
